/**
 * 航班监控上「发 AOG 通知」的判断。
 *
 * 机号去掉首尾空格再转成大写；机场必须是三位字母，不截断。
 * 停场机场只读目的站。人补上的机号只用于这封信，调用方不得写回航段。
 * 信在监控页的邮件编辑器里写完，收件人从「邮箱地址与分组」里选。
 */

const AIRPORT_CODE = /^[A-Z]{3}$/;

function escapeHtml(value) {
    return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

export function normalizeAogAircraft(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function normalizeAirportText(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function airportProblem(value) {
    if (!value) return "缺停场机场";
    if (!AIRPORT_CODE.test(value)) return "停场机场必须是三位字母";
    return null;
}

/**
 * @param {Array<{ aircraft_reg?: unknown, arrival_airport?: unknown }>} flights
 * @param {{ aircraft?: unknown, airport?: unknown }} [supply] 人在监控页补的值，只用于这封信
 */
export function resolveAogNotice(flights, supply = {}) {
    const legs = Array.isArray(flights) ? flights : [];
    if (legs.length === 0) return { status: "empty", message: "" };
    const aircrafts = legs.map((flight) => normalizeAogAircraft(flight?.aircraft_reg));
    const missingAircraft = aircrafts.some((value) => !value);
    let resolvedAircrafts = aircrafts;

    if (missingAircraft) {
        if (!Object.prototype.hasOwnProperty.call(supply, "aircraft")) {
            return {
                status: "needs_aircraft",
                message: "有的航段还没有机号。请补这封信的机号，不会改航班上的机号。",
            };
        }
        const filled = normalizeAogAircraft(supply.aircraft);
        if (!filled) {
            return {
                status: "needs_aircraft",
                message: "缺机号。请补这封信的机号，不会改航班上的机号。",
            };
        }
        resolvedAircrafts = aircrafts.map((value) => value || filled);
    }

    const uniqueAircrafts = [...new Set(resolvedAircrafts)];
    if (uniqueAircrafts.length !== 1 || !uniqueAircrafts[0]) {
        return { status: "multiple_aircraft", message: "一次只能通知一架飞机。" };
    }
    const aircraft = uniqueAircrafts[0];

    if (Object.prototype.hasOwnProperty.call(supply, "airport")) {
        const airport = normalizeAirportText(supply.airport);
        const problem = airportProblem(airport);
        if (problem) {
            return {
                status: "needs_airport",
                reason: airport ? "not_three_letters" : "missing",
                aircraft,
                message: `${problem}。请改成三位字母，不会截断，也不会另编一个机场。`,
            };
        }
        return { status: "ready", aircraft, airport };
    }

    const airports = legs.map((flight) => normalizeAirportText(flight?.arrival_airport));
    const uniqueAirports = [...new Set(airports)];
    if (uniqueAirports.length !== 1) {
        const choices = uniqueAirports.filter(Boolean);
        const seen = choices.length > 0 ? `现在看到的目的站有 ${choices.join("、")}。` : "";
        return {
            status: "needs_airport",
            reason: "multiple",
            aircraft,
            choices,
            message: `${seen}这些航段的目的站不是同一个。请指定停场机场，填三位字母。不会自动挑一段。`,
        };
    }

    const only = uniqueAirports[0] ?? "";
    const problem = airportProblem(only);
    if (problem) {
        const seen = only ? `现在的目的站是「${only}」。` : "";
        return {
            status: "needs_airport",
            reason: only ? "not_three_letters" : "missing",
            aircraft,
            current: only,
            message: `${seen}${problem}。请改成三位字母，不会截断，也不会另编一个机场。`,
        };
    }
    return { status: "ready", aircraft, airport: only };
}

export function buildAogNoticeContent(aircraft, airport) {
    const registration = escapeHtml(aircraft);
    const airportCode = escapeHtml(airport);
    const sentence = `A/C ${registration} is currently AOG @${airportCode}, further schedule update will be advised by address: schedule@flymeta.com once available.`;
    return {
        subject: `AOG // A/C ${aircraft} @${airport}`,
        html: `<div style="font-family: Arial, sans-serif; line-height: 1.6;"><p>Dear All,</p><p>${sentence}</p><p>BRGS,</p><p>FLYMETA OPS</p></div>`,
    };
}

function collectRecipients(emailData) {
    const recipients = [];
    const push = (list, type) => {
        (Array.isArray(list) ? list : []).forEach((email) => {
            const value = typeof email === "string" ? email.trim() : "";
            if (value) recipients.push({ email: value, type });
        });
    };
    push(emailData?.to, "TO");
    push(emailData?.cc, "CC");
    push(emailData?.bcc, "BCC");
    return recipients;
}

function flightIdsForLink(ids) {
    const seen = new Set();
    const result = [];
    (Array.isArray(ids) ? ids : []).forEach((id) => {
        const value = typeof id === "number" ? id : typeof id === "string" && /^\d+$/.test(id) ? Number(id) : null;
        if (!Number.isInteger(value) || value <= 0 || seen.has(value)) return;
        seen.add(value);
        result.push(value);
    });
    return result;
}

/**
 * 把编辑器里看到的内容收成监控页已有的发信请求。
 * 附件、预设发件账号、群发单显和已读回执都保留。
 * 选中的每一段都写进关联，正文以编辑器为准。
 */
export function buildAogMailRequest({ emailData, flightIds, fallbackHtml, senderUserId, sendingOptions }) {
    if (!Number.isInteger(senderUserId) || senderUserId <= 0) {
        return { ok: false, message: "当前登录状态无效，请重新登录后再发送" };
    }
    const recipients = collectRecipients(emailData);
    if (recipients.length === 0) return { ok: false, message: "请至少添加一个收件人" };
    const subject = typeof emailData?.subject === "string" ? emailData.subject : "";
    if (!subject.trim()) return { ok: false, message: "请填写邮件主题" };
    const bodyHtml = typeof emailData?.contentHtml === "string" ? emailData.contentHtml : fallbackHtml || "";
    if (!String(bodyHtml).trim()) return { ok: false, message: "请填写邮件正文" };

    const split = Boolean(sendingOptions?.bcc_sending);
    const ids = flightIdsForLink(flightIds);
    const senderKey = emailData?.selectedSender?.key;
    const request = {
        recipients,
        subject,
        body_html: bodyHtml,
        body_text: typeof emailData?.contentText === "string" && emailData.contentText ? emailData.contentText : null,
        attachments: Array.isArray(emailData?.attachments) ? emailData.attachments.filter((item) => item && typeof item === "object").map((item) => ({ ...item })) : [],
        sender_user_id: senderUserId,
        user_email_config_id: null,
        is_bulk_split: split,
        request_read_receipt: Boolean(sendingOptions?.read_receipt),
        send_interval: split ? 1.0 : 0.1,
        preset_sender_key: typeof senderKey === "string" && senderKey ? senderKey : null,
        content_block_context: emailData?.contentBlockContext && typeof emailData.contentBlockContext === "object" ? emailData.contentBlockContext : { instances: [] },
    };
    if (ids.length > 0) {
        request.business_link = {
            record_id: ids[0],
            record_type: "FLIGHT_OPERATION",
            link_type: "AOG_NOTICE",
            details: { flight_operation_ids: ids, batch_size: ids.length },
        };
    }
    return { ok: true, request };
}
